/**
 * FormConfig — what a form DOES when submitted. A form is a section whose grid
 * holds the fields (input widgets) and submit buttons; the form, not any one
 * field, owns the action, so moving or deleting a field never changes where
 * the others go.
 *
 * `FormAction` is a tagged union so new behaviours slot in without touching
 * inputs. Implemented today: `navigate` — a native GET to an external URL
 * (search boxes); the published page needs no JavaScript for it.
 *
 * Designed for later (docs/design/forms-and-lists.md): `createItem`, which
 * stores a submission as a row of a list — the list owns field types,
 * validation and permissions; `fieldBindings` maps list field ids to the
 * STABLE ids of input widgets (never their display names).
 */
import * as Schema from "effect/Schema";

/** An https URL — a form never sends visitors to plain http. */
export const HttpsUrl = Schema.String.pipe(
  Schema.filter(
    (s) => {
      try {
        return new URL(s).protocol === "https:";
      } catch {
        return false;
      }
    },
    { message: () => "Must be an https:// URL" },
  ),
);

export const NavigateAction = Schema.Struct({
  kind: Schema.Literal("navigate"),
  url: HttpsUrl,
  method: Schema.Literal("get"),
});
export type NavigateAction = Schema.Schema.Type<typeof NavigateAction>;

/** Every action a form can take. Only `navigate` exists today. */
export const FormAction = Schema.Union(NavigateAction);
export type FormAction = Schema.Schema.Type<typeof FormAction>;

export const FormConfig = Schema.Struct({
  /** Stable id, generated once; published as the <form> element id. */
  id: Schema.String.pipe(Schema.pattern(/^[A-Za-z][\w-]*$/)),
  action: FormAction,
});
export type FormConfig = Schema.Schema.Type<typeof FormConfig>;
